// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import ListItem, { UnorderedList } from "@insite/mobius/Lists";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { TypographyPresentationProps } from "@store/MobiusOverrides/Typography/Typography";
import constants from "@store/constants";
import useParentProductState from "@store/Framework/Store/Foundation/Data/Products/ProductsHooks/useParentProductState";
import AlertKishigo from "@store/Library/Components/Icons/AlertKishigo/AlertKishigo";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React, { FC, useState } from "react";
import { css } from "styled-components";

const enum fields {
    warningLink = "warningLink",
    warningLinkLabel = "warningLinkLabel",
    warningFilter = "warningFilter",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.warningLink]: string;
        [fields.warningLinkLabel]: string;
        [fields.warningFilter]: string;
    };
}

type Props = OwnProps & HasProduct;

export interface SizeChartStyles {
    linkWrapper: InjectableCss;
    modal?: ModalPresentationProps;
    warningWrapper: LinkPresentationProps;
    modalWrapper: InjectableCss;
    icon: IconPresentationProps;
    warningStyle: TypographyPresentationProps;
    listItemStyle: InjectableCss;
    modalList: InjectableCss;
}

export const primaryImageStyles: SizeChartStyles = {
    modalWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    modalList: {
        css: css`
            padding-left: ${pxToRem(115)};
        `,
    },
    listItemStyle: {
        css: css`
            text-align: left;
        `,
    },
    warningStyle: {
        css: css`
            font-weight: ${({ theme }) => theme.design.typography.weight.bold};
        `,
    },
    warningWrapper: {
        css: css`
            padding-top: ${pxToRem(20)};
            * {
                margin-right: ${pxToRem(2)};
            }
        `,
    },
    linkWrapper: {
        css: css`
            display: inline;
            padding-right: ${pxToRem(5)};
        `,
    },
    modal: {
        cssOverrides: {
            modalContainer: css`
                width: auto;
            `,
            modalBody: css`
                width: 350px;
                height: auto !important;
                padding: 32px;
            `,
            modalTitle: css`
                position: absolute;
                top: 0;
                right: 0;
                z-index: 1;
                padding: 0;
                border: 0;
            `,
            modalContent: css`
                padding: 0;
                text-align: center;
            `,
        },
        closeButtonProps: {
            shape: "rectangle",
            color: undefined,
            size: undefined,
            buttonType: undefined,
            css: css`
                border: 0;
                background-color: ${getColor("common.background")};
                padding: 0.5em;
                height: auto;
                transition: none;

                &:focus {
                    outline-offset: -${({ theme }) => theme?.focus?.width};
                }
            `,
        },
    },
    icon: {
        src: AlertKishigo,
        color: "warning.icon",
        css: css`
            position: relative;
            top: ${pxToRem(2.25)};
        `,
    },
};

const styles = primaryImageStyles;

const ProductSizingChart: FC<Props> = ({ product, fields }) => {
    const [warningIsOpen, setWarningModalIsOpen] = useState<boolean>(false);
    const parentProductState = useParentProductState();

    if (!product) {
        return null;
    }

    if (
        (!parentProductState?.attributeTypes || parentProductState?.attributeTypes.length === 0) &&
        (!product?.attributeTypes || product?.attributeTypes.length === 0)
    ) {
        return null;
    }

    const setWarning = parentProductState?.attributeTypes || product.attributeTypes;

    const warnings = setWarning
        ?.filter(w => w.name.includes(fields.warningFilter))
        .map(a => a.attributeValues || [])
        .shift();

    const noWarning = warnings?.filter(x => x.value === constants.productWarnings.prop65.none).length;

    if (!warnings || warnings.length === 0 || noWarning) {
        return null;
    }

    const renderWarnings = () => {
        return (
            <StyledWrapper {...styles.modalWrapper}>
                <StyledWrapper>
                    <Typography {...styles.warningStyle}>{translate("CALIFORNIA RESIDENTS")}</Typography>
                </StyledWrapper>
                <StyledWrapper>
                    <Icon {...styles.icon} />
                    {translate("WARNING:")}
                </StyledWrapper>
                <UnorderedList {...styles.modalList}>
                    {warnings?.map(w => (
                        <ListItem key={w.id} {...styles.listItemStyle}>
                            {w.valueDisplay}
                        </ListItem>
                    ))}
                </UnorderedList>
                <StyledWrapper>
                    <Link href={fields.warningLink} target="_blank">
                        {fields.warningLinkLabel}
                    </Link>
                </StyledWrapper>
            </StyledWrapper>
        );
    };

    return (
        <StyledWrapper>
            <StyledWrapper {...styles.warningWrapper}>
                <Typography {...styles.warningStyle}>
                    {translate("California Residents: ")}
                    <Icon {...styles.icon} />
                    {translate("Warning: ")}
                </Typography>
                <StyledWrapper {...styles.linkWrapper}>
                    <Link
                        onClick={() => {
                            setWarningModalIsOpen(true);
                        }}
                    >
                        {translate("Proposition 65")}
                    </Link>
                </StyledWrapper>
            </StyledWrapper>
            <Modal {...styles.modal} isOpen={warningIsOpen} handleClose={() => setWarningModalIsOpen(false)}>
                {renderWarnings()}
            </Modal>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(ProductSizingChart),
    definition: {
        displayName: "Warning Badge",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
        fieldDefinitions: [
            {
                name: fields.warningLinkLabel,
                displayName: "warning link label",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "",
                isRequired: false,
            },
            {
                name: fields.warningLink,
                displayName: "warning link",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "",
                isRequired: false,
            },
            {
                name: fields.warningFilter,
                displayName: "warning filter",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "",
                isRequired: false,
            },
        ],
    },
};

export default widgetModule;
